import Button from '@rio-cloud/rio-uikit/Button';
import { useId } from 'react';
import { useIntl } from 'react-intl';

import type { ChargingConfiguration } from '#domain/ChargingDetails.ts';

type Props = { value?: ChargingConfiguration; onChange: (value: ChargingConfiguration | undefined) => void };

export const ChargingConfigurationFields = ({ value, onChange }: Props) => {
    const intl = useIntl();
    const id = useId();
    const label = (key: string) => intl.formatMessage({ id: `intl-msg:ordercom-cloud-frontend.charging.${key}` });
    if (!value) {
        return (
            <Button type='button' onClick={() => onChange({ mode: 'target_percentage', target_percentage: 80 })}>
                {label('configure')}
            </Button>
        );
    }
    const numeric = (
        key: 'target_percentage' | 'charging_duration_seconds' | 'charger_power_kw' | 'voltage_v' | 'current_a',
        title: string,
        max: number,
        required = false
    ) => (
        <div className='form-group'>
            <label htmlFor={`${id}-${key}`}>{label(title)}</label>
            <input
                id={`${id}-${key}`}
                className='form-control'
                type='number'
                min={key === 'charging_duration_seconds' ? 1 : 0.01}
                max={max}
                step={key === 'charging_duration_seconds' ? 1 : 'any'}
                required={required}
                value={value[key] ?? ''}
                onChange={event =>
                    onChange({ ...value, [key]: event.target.value === '' ? undefined : Number(event.target.value) })
                }
            />
        </div>
    );
    return (
        <fieldset className='margin-top-15'>
            <legend>{label('title')}</legend>
            <div className='form-group'>
                <label htmlFor={`${id}-mode`}>{label('mode')}</label>
                <select
                    id={`${id}-mode`}
                    className='form-control'
                    value={value.mode}
                    onChange={event =>
                        onChange(
                            event.target.value === 'target_percentage'
                                ? {
                                      ...value,
                                      mode: 'target_percentage',
                                      target_percentage: 80,
                                      charging_duration_seconds: undefined,
                                  }
                                : {
                                      ...value,
                                      mode: 'fixed_duration',
                                      target_percentage: undefined,
                                      charging_duration_seconds: 1800,
                                  }
                        )
                    }
                >
                    <option value='target_percentage'>{label('target')}</option>
                    <option value='fixed_duration'>{label('duration')}</option>
                </select>
            </div>
            <div className='display-grid grid-cols-2 gap-10'>
                {value.mode === 'target_percentage'
                    ? numeric('target_percentage', 'target', 100, true)
                    : numeric('charging_duration_seconds', 'duration', 86400, true)}
                {numeric('charger_power_kw', 'power', 2000)}
                {numeric('voltage_v', 'voltage', 2000)}
                {numeric('current_a', 'current', 5000)}
                <div className='form-group'>
                    <label htmlFor={`${id}-connector`}>{label('connector')}</label>
                    <select
                        id={`${id}-connector`}
                        className='form-control'
                        value={value.connector_type ?? ''}
                        onChange={event =>
                            onChange({
                                ...value,
                                connector_type:
                                    (event.target.value as ChargingConfiguration['connector_type']) || undefined,
                            })
                        }
                    >
                        <option value=''>{label('unknown')}</option>
                        <option value='iec62196Type2Combo'>CCS2</option>
                        <option value='iec62196Type2'>Type 2</option>
                        <option value='chademo'>CHAdeMO</option>
                    </select>
                </div>
                <div className='form-group'>
                    <label htmlFor={`${id}-supply`}>{label('supply')}</label>
                    <select
                        id={`${id}-supply`}
                        className='form-control'
                        value={value.supply_type ?? ''}
                        onChange={event =>
                            onChange({
                                ...value,
                                supply_type: (event.target.value as ChargingConfiguration['supply_type']) || undefined,
                            })
                        }
                    >
                        <option value=''>{label('unknown')}</option>
                        <option value='dc'>DC</option>
                        <option value='acSingle'>AC 1</option>
                        <option value='acThree'>AC 3</option>
                    </select>
                </div>
            </div>
            <p className='text-size-12'>{label('optional-capabilities')}</p>
            <Button type='button' onClick={() => onChange(undefined)}>
                {label('clear')}
            </Button>
        </fieldset>
    );
};
